"use client"

import { useState, useContext, useEffect, useMemo, useCallback, useRef } from "react"
import {
  Container,
  Autocomplete,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Chip,
  Alert,
  Divider,
  Tabs,
  Tab,
  Card,
  CardContent,
  Tooltip,
  InputAdornment,
  IconButton,
} from "@mui/material"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Security as SecurityIcon,
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material"
import { ErpContext } from "../../context/ErpContext.js"
import UserForm from "./UserForm.js"
import TablaUsuariosComponent from "./TablaUsuariosComponent.js"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`accesos-tabpanel-${index}`}
      aria-labelledby={`accesos-tab-${index}`}
      {...other}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      {value === index && (
        <Box 
          sx={{ 
            p: 0, 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0
          }}
        >
          {children}
        </Box>
      )}
    </div>
  )
}

const GestionarAccesosComponent = () => {
  const [tabValue, setTabValue] = useState(0)
  const [roles, setRoles] = useState([])
  const [rolSeleccionado, setRolSeleccionado] = useState(null)
  const [menuAccesos, setMenuAccesos] = useState([])
  const [componentesSector, setComponentesSector] = useState([])
  const [opcionesAcceso, setOpcionesAcceso] = useState([])
  const [expandedPanels, setExpandedPanels] = useState({})
  const [expandedComponentesSector, setExpandedComponentesSector] = useState({})
  const [accesosOriginales, setAccesosOriginales] = useState([])
  const [accesosFuncionalidadOriginales, setAccesosFuncionalidadOriginales] = useState([])
  const [showSuccess, setShowSuccess] = useState(false)
  const [filtroRoles, setFiltroRoles] = useState("")
  const [loadingAccesos, setLoadingAccesos] = useState(false)
  const componentesSectorCargadosRef = useRef(false)

  const { getRolUsuario, postRolUsuario, updateRolUsuario, getUsuariosByRol, deleteRolYUsuarios, getMenuAcceso, getRolAccesoByIdRol, actualizarRolAccesos, getComponenteSector, getRolSectorAccesoByIdRol, actualizarRolSectorAccesos, postUsuario } = useContext(ErpContext)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const ErrorModal = () => {
    withReactContent(Swal).fire({
      position: "bottom-end",
      icon: "error",
      text: "Error al crear el usuario",
      showConfirmButton: false,
      timer: 3000,
      width: 300,
    })
  }

  const SucessModal = () => {
    withReactContent(Swal).fire({
      position: "bottom-end",
      icon: "success",
      text: "Usuario creado correctamente",
      showConfirmButton: false,
      timer: 3000,
      width: 300,
    })
  }

  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    try {
      const [roles, menu, componentes] = await Promise.all([
        getRolUsuario(),
        getMenuAcceso(),
        getComponenteSector(),
      ])
      setRoles(roles)
      setMenuAccesos(menu)
      setComponentesSector(componentes || [])
      componentesSectorCargadosRef.current = true
    } catch (error) {
      console.error("Error al obtener datos iniciales:", error)
    }
  }

  // Estados extra para crear rol y guardar
  const [nuevoRol, setNuevoRol] = useState("")
  const [creandoRol, setCreandoRol] = useState(false)
  const [crearRolError, setCrearRolError] = useState(null)
  const [crearRolSuccess, setCrearRolSuccess] = useState(null)

  const [guardando, setGuardando] = useState(false)
  const [guardarError, setGuardarError] = useState(null)
  const [guardarSuccess, setGuardarSuccess] = useState(null)
  const [openRolesModal, setOpenRolesModal] = useState(false)
  const [rolOrigen, setRolOrigen] = useState(null)
  const [rolDestino, setRolDestino] = useState(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [usuariosAEliminar, setUsuariosAEliminar] = useState([])
  const [rolAEliminar, setRolAEliminar] = useState(null)

  // Función para construir la estructura jerárquica dinámicamente (memoizada)
  const construirEstructuraJerarquica = useCallback((menuAccesos, accesosDelRol, componentesSector, accesosFuncionalidadDelRol) => {
    // console.log("=== CONSTRUCCIÓN DE ESTRUCTURA JERÁRQUICA ===")
    // console.log("componentesSector recibido (raw):", componentesSector)
    // console.log("Cantidad de componentes recibidos:", componentesSector?.length || 0)
    
    const menusFiltrados = menuAccesos.filter((menu) => !menu.eliminado)
    const componentesFiltrados = componentesSector.filter((comp) => !comp.eliminado)

    // console.log("Total menús disponibles:", menusFiltrados.map(m => m.id))
    // console.log("Componentes después de filtrar por eliminado:", componentesFiltrados)
    // console.log("Total componentes sector:", componentesFiltrados.map(c => `${c.id} -> ${c.idMenuAcceso}`))

    // Separar menús principales de submenús
    const menusPrincipales = menusFiltrados.filter((menu) => {
      const id = menu.id
      // DASHBOARD es especial (sin hijos)
      if (id === "DASHBOARD") return true
      // Los demás principales no tienen números al final
      return !/\d+$/.test(id) && id !== "DASHBOARD"
    })
    
    // console.log("Menús identificados como principales:", menusPrincipales.map(m => m.id))

    // Helper para obtener componentes sector de un menú
    const obtenerComponentesSector = (idMenuAcceso) => {
      if (!idMenuAcceso) return []
      
      // Normalizar comparación: trim y comparar strings
      const idMenuNormalizado = String(idMenuAcceso).trim()
      const componentesDelMenu = componentesFiltrados.filter((comp) => {
        const compIdMenu = String(comp.idMenuAcceso || '').trim()
        return compIdMenu === idMenuNormalizado
      })
      
      // console.log(`Componentes sector para "${idMenuAcceso}":`, componentesDelMenu)
      // console.log(`Total componentes filtrados: ${componentesFiltrados.length}, Encontrados: ${componentesDelMenu.length}`)
      
      if (componentesDelMenu.length === 0 && componentesFiltrados.length > 0) {
        console.warn(`⚠️ No se encontraron componentes para "${idMenuAcceso}". Verifica que el campo idMenuAcceso sea exactamente "${idMenuAcceso}"`)
        // console.log("Componentes disponibles y sus idMenuAcceso:", componentesFiltrados.map(c => ({ id: c.id, idMenuAcceso: c.idMenuAcceso })))
      }
      
      return componentesDelMenu.map((comp) => {
        const tieneAcceso = accesosFuncionalidadDelRol.some(
          (a) => {
            const aIdMenu = String(a.idMenuAcceso || '').trim()
            const aIdSector = String(a.idSector || '').trim()
            const compId = String(comp.id || '').trim()
            return aIdMenu === idMenuNormalizado && aIdSector === compId && a.eliminado === false
          }
        )
        return {
          id: comp.id,
          label: comp.descripcion || comp.id, // Usar descripción, o el id como fallback
          tipo: comp.tipo || 'componente',
          activo: tieneAcceso,
          esComponenteSector: true,
        }
      })
    }

    // Construir estructura jerárquica
    const estructuraJerarquica = menusPrincipales.map((menuPrincipal) => {
      let children = []

      // Si no es DASHBOARD, buscar sus hijos
      if (menuPrincipal.id !== "DASHBOARD") {
        const posiblesHijos = menusFiltrados.filter((menu) => {
          // Los hijos tienen el mismo prefijo + número
          const esHijo = menu.id.startsWith(menuPrincipal.id) && /\d+$/.test(menu.id)
          return esHijo
        })
        
        // console.log(`Hijos encontrados para ${menuPrincipal.id}:`, posiblesHijos.map(h => h.id))
        
        children = posiblesHijos.map((child) => {
          const tieneAcceso = accesosDelRol.some((a) => a.idMenuAcceso === child.id && a.eliminado === false)
          // Agregar componentes sector asociados a este submenú
          // console.log(`Buscando componentes sector para submenú: ${child.id}`)
          const componentesSectorChild = obtenerComponentesSector(child.id)
          // console.log(`Componentes encontrados para ${child.id}:`, componentesSectorChild)
          return {
            id: child.id,
            label: child.descripcion,
            activo: tieneAcceso,
            componentesSector: componentesSectorChild,
          }
        })
      }

      // Verificar si el menú principal tiene acceso
      const tieneAccesoMenuPrincipal = accesosDelRol.some(
        (a) => a.idMenuAcceso === menuPrincipal.id && a.eliminado === false,
      )

      // Agregar componentes sector asociados al menú principal
      const componentesSectorPrincipal = obtenerComponentesSector(menuPrincipal.id)

      return {
        id: menuPrincipal.id,
        label: menuPrincipal.descripcion,
        activo: tieneAccesoMenuPrincipal,
        children: children,
        componentesSector: componentesSectorPrincipal,
      }
    })

    return estructuraJerarquica
  }, [])

  const cargarAccesosDelRol = useCallback(async (idRol) => {
    if (!idRol || menuAccesos.length === 0) {
      setOpcionesAcceso([])
      return
    }

    setLoadingAccesos(true)
    try {
      // Asegurar que tengamos los componentes sector cargados (solo si realmente están vacíos)
      let componentesActuales = componentesSector
      if (!componentesSectorCargadosRef.current || !componentesActuales || componentesActuales.length === 0) {
        componentesActuales = await getComponenteSector()
        setComponentesSector(componentesActuales || [])
        componentesSectorCargadosRef.current = true
      }

      const [accesosDelRol, accesosFuncionalidadDelRol] = await Promise.all([
        getRolAccesoByIdRol(idRol),
        getRolSectorAccesoByIdRol(idRol),
      ])

      // Guardar accesos originales para comparación posterior
      setAccesosOriginales(accesosDelRol)
      setAccesosFuncionalidadOriginales(accesosFuncionalidadDelRol || [])

      // Construir estructura jerárquica dinámicamente
      const estructuraJerarquica = construirEstructuraJerarquica(
        menuAccesos,
        accesosDelRol,
        componentesActuales || [],
        accesosFuncionalidadDelRol || []
      )
      setOpcionesAcceso(estructuraJerarquica)
    } catch (error) {
      console.error("Error al obtener accesos del rol:", error)
      setOpcionesAcceso([])
    } finally {
      setLoadingAccesos(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuAccesos, construirEstructuraJerarquica])

  useEffect(() => {
    if (rolSeleccionado?.id && menuAccesos.length > 0) {
      cargarAccesosDelRol(rolSeleccionado.id)
    } else {
      setOpcionesAcceso([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolSeleccionado?.id])

  const handleToggleAccesoMenuPrincipal = (menuId) => {
    setOpcionesAcceso((prev) =>
      prev.map((menu) => {
        if (menu.id === menuId) {
          const nuevoEstado = !menu.activo
          // Si se activa el menú principal, activar todos los hijos
          // Si se desactiva el menú principal, desactivar todos los hijos
          const childrenActualizados = menu.children.map((child) => ({
            ...child,
            activo: nuevoEstado,
          }))

          return {
            ...menu,
            activo: nuevoEstado,
            children: childrenActualizados,
          }
        }
        return menu
      }),
    )
  }

  const handleToggleAccesoChild = (menuId, childId) => {
    setOpcionesAcceso((prev) =>
      prev.map((menu) => {
        if (menu.id === menuId) {
          const childrenActualizados = menu.children.map((child) =>
            child.id === childId ? { ...child, activo: !child.activo } : child,
          )

          // Si se activa un hijo, activar también el menú principal
          // Si se desactiva un hijo, verificar si quedan hijos activos para mantener el padre activo
          const algunHijoActivo = childrenActualizados.some((child) => child.activo)

          return {
            ...menu,
            activo: algunHijoActivo, // El padre solo está activo si hay al menos un hijo activo
            children: childrenActualizados,
          }
        }
        return menu
      }),
    )
  }

  const handleToggleComponenteSector = (idMenuAcceso, componenteId) => {
    setOpcionesAcceso((prev) =>
      prev.map((menu) => {
        // Verificar si es el menú principal
        if (menu.id === idMenuAcceso) {
          const componentesActualizados = (menu.componentesSector || []).map((comp) =>
            comp.id === componenteId ? { ...comp, activo: !comp.activo } : comp
          )
          return {
            ...menu,
            componentesSector: componentesActualizados,
          }
        }
        // Buscar en los children
        const childrenActualizados = menu.children.map((child) => {
          if (child.id === idMenuAcceso) {
            const componentesActualizados = (child.componentesSector || []).map((comp) =>
              comp.id === componenteId ? { ...comp, activo: !comp.activo } : comp
            )
            return {
              ...child,
              componentesSector: componentesActualizados,
            }
          }
          return child
        })
        // Si se modificaron los children, retornar el menu actualizado
        if (JSON.stringify(childrenActualizados) !== JSON.stringify(menu.children)) {
          return {
            ...menu,
            children: childrenActualizados,
          }
        }
        return menu
      }),
    )
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }))
  }

  const contarAccesosActivos = (menu) => {
    const menuPrincipalActivo = menu.activo ? 1 : 0
    const hijosActivos = menu.children.filter((child) => child.activo).length
    const componentesSectorActivos = (menu.componentesSector || []).filter((comp) => comp.activo).length
    return menuPrincipalActivo + hijosActivos + componentesSectorActivos
  }

  const prepararDatosParaGuardar = () => {
    const accesosParaGuardar = []
    const accesosFuncionalidadParaGuardar = []

    // Obtener todos los IDs de accesos actuales (activos)
    const accesosActivosActuales = new Set()
    const accesosFuncionalidadActivosActuales = new Set()

    opcionesAcceso.forEach((menu) => {
      // Agregar menú principal si está activo
      if (menu.activo) {
        accesosActivosActuales.add(menu.id)
        accesosParaGuardar.push({
          id: menu.id,
          activo: true,
        })
      }

      // Agregar componentes sector del menú principal
      (menu.componentesSector || []).forEach((comp) => {
        const key = `${menu.id}:${comp.id}`
        if (comp.activo) {
          accesosFuncionalidadActivosActuales.add(key)
          accesosFuncionalidadParaGuardar.push({
            idMenuAcceso: menu.id,
            idSector: comp.id,
            activo: true,
          })
        }
      })

      // Agregar hijos activos
      menu.children.forEach((child) => {
        if (child.activo) {
          accesosActivosActuales.add(child.id)
          accesosParaGuardar.push({
            id: child.id,
            activo: true,
          })
        }

        // Agregar componentes sector de los hijos
        (child.componentesSector || []).forEach((comp) => {
          const key = `${child.id}:${comp.id}`
          if (comp.activo) {
            accesosFuncionalidadActivosActuales.add(key)
            accesosFuncionalidadParaGuardar.push({
              idMenuAcceso: child.id,
              idSector: comp.id,
              activo: true,
            })
          }
        })
      })
    })

    // Comparar con accesos originales para encontrar los que se desactivaron
    accesosOriginales.forEach((accesoOriginal) => {
      // Si el acceso estaba activo antes pero ahora no está en los activos actuales
      if (!accesoOriginal.eliminado && !accesosActivosActuales.has(accesoOriginal.idMenuAcceso)) {
        accesosParaGuardar.push({
          id: accesoOriginal.idMenuAcceso,
          activo: false, // Marcar como inactivo para eliminar
        })
      }
    })

    // Comparar con accesos funcionalidad originales para encontrar los que se desactivaron
    accesosFuncionalidadOriginales.forEach((accesoFuncionalidadOriginal) => {
      const key = `${accesoFuncionalidadOriginal.idMenuAcceso}:${accesoFuncionalidadOriginal.idSector}`
      if (!accesoFuncionalidadOriginal.eliminado && !accesosFuncionalidadActivosActuales.has(key)) {
        accesosFuncionalidadParaGuardar.push({
          idMenuAcceso: accesoFuncionalidadOriginal.idMenuAcceso,
          idSector: accesoFuncionalidadOriginal.idSector,
          activo: false,
        })
      }
    })

    return { accesosParaGuardar, accesosFuncionalidadParaGuardar }
  }

  const handleGuardarCambios = async () => {
    if (!rolSeleccionado?.id) return;
    const { accesosParaGuardar, accesosFuncionalidadParaGuardar } = prepararDatosParaGuardar()
    setGuardando(true)
    setGuardarError(null)
    setGuardarSuccess(null)
    try {
      await Promise.all([
        actualizarRolAccesos(rolSeleccionado.id, accesosParaGuardar),
        actualizarRolSectorAccesos(rolSeleccionado.id, accesosFuncionalidadParaGuardar),
      ])
      setGuardarSuccess("Accesos actualizados correctamente")
      await cargarAccesosDelRol(rolSeleccionado.id)
    } catch (error) {
      setGuardarError("Error al actualizar accesos")
    } finally {
      setGuardando(false)
    }
  }

  // Función para determinar el estado del checkbox padre
  const getParentCheckboxState = (menu) => {
    const hijosActivos = menu.children.filter((child) => child.activo).length
    const totalHijos = menu.children.length

    if (totalHijos === 0) return menu.activo // Para menús sin hijos (DASHBOARD)
    if (hijosActivos === 0) return false
    if (hijosActivos === totalHijos) return true
    return "indeterminate" // Estado intermedio cuando algunos hijos están activos
  }

  

  return (
    <Container maxWidth="100%" sx={{ mt: 0, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f8f9fa" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "#5b5b5f",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "16px",
                minHeight: "60px",
                "&.Mui-selected": {
                  color: "#5b5b5f",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#5b5b5f",
                height: 3,
              },
            }}
          >
            <Tab icon={<SecurityIcon />} label="Roles" iconPosition="start" sx={{ mr: 2 }} />
            <Tab icon={<PersonAddIcon />} label="Crear Usuario" iconPosition="start" />
            <Tab icon={<PeopleIcon />} label="Gestionar Usuarios" iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Administrar Roles y Crear nuevo rol - en la misma línea */}
            <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center", paddingTop: 1, flexShrink: 0 }}>
              <TextField
                size="small"
                label="Nuevo rol"
                value={nuevoRol}
                onChange={(e) => setNuevoRol(e.target.value)}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                disabled={creandoRol || !nuevoRol.trim()}
                onClick={async () => {
                  setCreandoRol(true)
                  setCrearRolError(null)
                  setCrearRolSuccess(null)
                  try {
                    const creado = await postRolUsuario(nuevoRol.trim())
                    const rolesActualizados = await getRolUsuario()
                    setRoles(rolesActualizados || [])
                    const rolCreado = rolesActualizados?.find((r) => r.id === creado.id) || null
                    setRolSeleccionado(rolCreado)
                    setCrearRolSuccess("Rol creado correctamente")
                    setNuevoRol("")
                  } catch (e) {
                    setCrearRolError("No se pudo crear el rol")
                  } finally {
                    setCreandoRol(false)
                  }
                }}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                {creandoRol ? <CircularProgress size={20} color="inherit" /> : "Crear rol"}
              </Button>
              <Button 
                variant="contained" 
                size="small" 
                startIcon={<SettingsIcon />}
                onClick={() => setOpenRolesModal(true)}
                sx={{
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 2,
                  py: 0.5,
                  boxShadow: 1,
                  "&:hover": {
                    backgroundColor: "#4a4a4e",
                    boxShadow: 2,
                  },
                  transition: "all 0.2s ease-in-out",
                  whiteSpace: "nowrap",
                }}
              >
                Administrar Roles
              </Button>
            </Box>
      {crearRolError && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setCrearRolError(null)}>
          {crearRolError}
        </Alert>
      )}
      {crearRolSuccess && (
        <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setCrearRolSuccess(null)}>
          {crearRolSuccess}
        </Alert>
      )}

      <Autocomplete
        size="small"
        options={roles}
        getOptionLabel={(option) => option.descripcion}
        value={rolSeleccionado}
        onChange={(e, value) => setRolSeleccionado(value)}
        renderInput={(params) => <TextField {...params} label="Seleccionar Rol" fullWidth />}
        sx={{ marginBottom: 3, flexShrink: 0, marginTop: 2 }}
      />

      {rolSeleccionado && (
        <Paper 
          elevation={2} 
          sx={{ 
            padding: 1.5,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, justifyContent: 'space-between', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                Accesos para rol: <strong>{rolSeleccionado.descripcion}</strong>
              </Typography>
              {!loadingAccesos && (
                <Chip
                  label={`${opcionesAcceso.reduce((total, menu) => total + contarAccesosActivos(menu), 0)} accesos activos`}
                  color="primary"
                  size="small"
                />
              )}
              {loadingAccesos && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Cargando accesos...
                  </Typography>
                </Box>
              )}
            </Box>
            <span />
          </Box>

          {loadingAccesos && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingAccesos && opcionesAcceso.length === 0 && (
            <Alert severity="info" sx={{ mb: 1, flexShrink: 0, py: 0.5 }}>
              No se encontraron opciones de acceso para este rol.
            </Alert>
          )}

          <Box 
            sx={{ 
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 0,
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
            }}
          >
          {!loadingAccesos && opcionesAcceso.map((menu) => {
            const parentState = getParentCheckboxState(menu)

            return (
              <Accordion
                key={menu.id}
                expanded={expandedPanels[menu.id] || false}
                onChange={handleAccordionChange(menu.id)}
                sx={{ 
                  mb: 0.5,
                  boxShadow: 'none',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: '0 0 4px 0',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={(menu.children.length > 0 || (menu.componentesSector && menu.componentesSector.length > 0)) ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : null}
                  sx={{
                    minHeight: 40,
                    maxHeight: 40,
                    backgroundColor: menu.activo ? "rgba(25, 118, 210, 0.08)" : "inherit",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: '8px 0',
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={parentState === true}
                          indeterminate={parentState === "indeterminate"}
                          onChange={() => handleToggleAccesoMenuPrincipal(menu.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          sx={{ py: 0 }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" fontWeight={menu.activo ? 600 : 400} sx={{ fontSize: '0.875rem' }}>
                            {menu.label}
                          </Typography>
                          {menu.children.length > 0 && (
                            <Chip
                              label={`${menu.children.filter((child) => child.activo).length}/${menu.children.length}`}
                              size="small"
                              color={menu.children.some((child) => child.activo) ? "primary" : "default"}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {menu.componentesSector && menu.componentesSector.length > 0 && (
                            <Chip
                              label={`${menu.componentesSector.filter((comp) => comp.activo).length}/${menu.componentesSector.length}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      onClick={(e) => e.stopPropagation()}
                      sx={{ margin: 0 }}
                    />
                  </Box>
                </AccordionSummary>

                {(menu.children.length > 0 || (menu.componentesSector && menu.componentesSector.length > 0)) && (
                  <AccordionDetails sx={{ pt: 1, pb: 1, px: 2 }}>
                    {menu.children.length > 0 && (
                      <FormGroup sx={{ mb: menu.componentesSector && menu.componentesSector.length > 0 ? 1.5 : 0 }}>
                        {menu.children.map((child) => (
                          <Box key={child.id} sx={{ mb: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={child.activo}
                                  onChange={() => handleToggleAccesoChild(menu.id, child.id)}
                                  size="small"
                                  sx={{ py: 0 }}
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <Typography
                                    variant="body2"
                                    color={child.activo ? "primary" : "text.secondary"}
                                    fontWeight={child.activo ? 500 : 400}
                                    sx={{ fontSize: '0.8125rem' }}
                                  >
                                    {child.label}
                                  </Typography>
                                  {child.componentesSector && child.componentesSector.length > 0 && (
                                    <Chip
                                      label={`${child.componentesSector.filter((comp) => comp.activo).length}/${child.componentesSector.length}`}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                              }
                              sx={{ ml: 0.5, margin: 0 }}
                            />
                            {child.componentesSector && child.componentesSector.length > 0 && (
                              <Accordion 
                                expanded={expandedComponentesSector[`${child.id}-sector`] || false}
                                onChange={(e, isExpanded) => setExpandedComponentesSector(prev => ({ ...prev, [`${child.id}-sector`]: isExpanded }))}
                                sx={{ 
                                  ml: 3, 
                                  mt: 0.25, 
                                  mb: 0.5,
                                  boxShadow: 'none', 
                                  border: '1px solid rgba(0, 0, 0, 0.06)',
                                  borderRadius: 0.5,
                                  '&:before': { display: 'none' },
                                  '&.Mui-expanded': {
                                    margin: '2px 0 4px 12px',
                                  }
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                  sx={{ minHeight: 28, maxHeight: 28, py: 0 }}
                                >
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    Componentes sector ({child.componentesSector.filter((comp) => comp.activo).length}/{child.componentesSector.length})
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1.5 }}>
                                  <FormGroup>
                                    {child.componentesSector.map((comp) => (
                                      <FormControlLabel
                                        key={comp.id}
                                        control={
                                          <Checkbox
                                            checked={comp.activo || false}
                                            onChange={() => handleToggleComponenteSector(child.id, comp.id)}
                                            size="small"
                                            sx={{ py: 0 }}
                                          />
                                        }
                                        label={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Typography
                                              variant="caption"
                                              color={comp.activo ? "secondary.main" : "text.secondary"}
                                              fontWeight={comp.activo ? 500 : 400}
                                              sx={{ fontSize: '0.75rem' }}
                                            >
                                              {comp.label || comp.descripcion || comp.id}
                                            </Typography>
                                            {comp.tipo && (
                                              <Chip
                                                label={comp.tipo}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 16, fontSize: '0.6rem' }}
                                              />
                                            )}
                                          </Box>
                                        }
                                        sx={{ ml: 0.5, margin: 0, mb: 0.25 }}
                                      />
                                    ))}
                                  </FormGroup>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Box>
                        ))}
                      </FormGroup>
                    )}
                    {menu.componentesSector && menu.componentesSector.length > 0 && (
                      <Accordion
                        expanded={expandedComponentesSector[`${menu.id}-sector`] || false}
                        onChange={(e, isExpanded) => setExpandedComponentesSector(prev => ({ ...prev, [`${menu.id}-sector`]: isExpanded }))}
                        sx={{ 
                          mt: menu.children.length > 0 ? 1 : 0,
                          borderTop: menu.children.length > 0 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                          pt: menu.children.length > 0 ? 1 : 0,
                          boxShadow: 'none',
                          border: '1px solid rgba(0, 0, 0, 0.06)',
                          borderRadius: 1,
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            marginTop: menu.children.length > 0 ? '8px' : '0',
                          }
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                          sx={{ minHeight: 36, maxHeight: 36 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
                              Componentes sector
                            </Typography>
                            <Chip
                              label={`${menu.componentesSector.filter((comp) => comp.activo).length}/${menu.componentesSector.length}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1.5 }}>
                          <FormGroup>
                            {menu.componentesSector.map((comp) => (
                              <FormControlLabel
                                key={comp.id}
                                control={
                                  <Checkbox
                                    checked={comp.activo}
                                    onChange={() => handleToggleComponenteSector(menu.id, comp.id)}
                                    size="small"
                                    sx={{ py: 0 }}
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography
                                      variant="body2"
                                      color={comp.activo ? "secondary.main" : "text.secondary"}
                                      fontWeight={comp.activo ? 500 : 400}
                                      sx={{ fontSize: '0.8125rem' }}
                                    >
                                      {comp.label || comp.descripcion || comp.id}
                                    </Typography>
                                    {comp.tipo && (
                                      <Chip
                                        label={comp.tipo}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Box>
                                }
                                sx={{ ml: 0.5, margin: 0, mb: 0.25 }}
                              />
                            ))}
                          </FormGroup>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </AccordionDetails>
                )}
              </Accordion>
            )
          })}
          </Box>

          <Box sx={{ marginTop: 1.5, display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}>
            <Button variant="contained" onClick={handleGuardarCambios} disabled={guardando}>
              {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar cambios"}
            </Button>
            <Button variant="outlined" onClick={() => cargarAccesosDelRol(rolSeleccionado.id)} disabled={guardando}>
              Recargar
            </Button>
            {guardarError && (
              <Alert severity="error" onClose={() => setGuardarError(null)} sx={{ ml: 2 }}>
                {guardarError}
              </Alert>
            )}
            {guardarSuccess && (
              <Alert severity="success" onClose={() => setGuardarSuccess(null)} sx={{ ml: 2 }}>
                {guardarSuccess}
              </Alert>
            )}
          </Box>
        </Paper>
      )}
        </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card 
            elevation={2} 
            sx={{ 
              borderRadius: 2, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <CardContent 
              sx={{ 
                p: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, flexShrink: 0 }}>
                <PersonAddIcon sx={{ color: "#5b5b5f", mr: 1.5, fontSize: 24 }} />
                <Typography variant="h6" sx={{ color: "#5b5b5f", fontWeight: 600 }}>
                  Nuevo Usuario
                </Typography>
              </Box>

              {showSuccess && (
                <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2, flexShrink: 0 }}>
                  ¡Usuario creado exitosamente!
                </Alert>
              )}

              <Box 
                sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '10px',
                    '&:hover': {
                      backgroundColor: '#555',
                    },
                  },
                }}
              >
                <UserForm
                mode="create"
                formId="create-user-form"
                initialValues={{}}
                onSubmit={async (payload) => {
                  const res = await postUsuario(
                    payload.usuario,
                    payload.nombre,
                    payload.apellido,
                    payload.telefono,
                    payload.email,
                    payload.direccion,
                    payload.localidad,
                    payload.provincia,
                    payload.password,
                    payload.rol,
                    payload.dniCuitCuil,
                  )
                  if (!res || res.success !== true) throw new Error("Error al crear usuario")
                }}
                onSuccess={() => {
                  setShowSuccess(true)
                  setTimeout(() => setShowSuccess(false), 3000)
                  SucessModal()
                }}
                onError={() => {
                  ErrorModal()
                }}
                renderActions={({ resetForm, submitting }) => (
                  <>
                    <Button
                      variant="outlined"
                      onClick={resetForm}
                      sx={{
                        borderColor: "#5b5b5f",
                        color: "#5b5b5f",
                        "&:hover": {
                          borderColor: "#5b5b5f",
                          backgroundColor: "rgba(91, 91, 95, 0.04)",
                        },
                      }}
                    >
                      Limpiar
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      startIcon={<PersonAddIcon />}
                      disabled={submitting}
                      sx={{
                        backgroundColor: "#5b5b5f",
                        "&:hover": { backgroundColor: "#4a4a4e" },
                        px: 4,
                      }}
                    >
                      Crear Usuario
                    </Button>
                  </>
                )}
              />
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel sx={{ height: '100%' }} value={tabValue} index={2}>
          <TablaUsuariosComponent />
        </TabPanel>

      {/* Modal de administración de roles (siempre montado) */}
      <Dialog open={openRolesModal} onClose={() => setOpenRolesModal(false)} fullWidth maxWidth="sm">
        <DialogTitle
          component="div"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon sx={{ color: "#5b5b5f", fontSize: 20 }} />
            <Typography component="span" variant="h6" sx={{ fontSize: '1.1rem' }}>
              Administración de Roles
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setOpenRolesModal(false)}
            sx={{
              color: "#5b5b5f",
              "&:hover": {
                backgroundColor: "rgba(91, 91, 95, 0.1)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5, overflow: 'visible' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#5b5b5f", fontSize: '0.9rem' }}>Editar descripciones</Typography>
          
          {/* Filtro de búsqueda */}
          <TextField
            size="small"
            placeholder="Buscar rol por descripción..."
            value={filtroRoles}
            onChange={(e) => setFiltroRoles(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <Box 
            sx={{ 
              maxHeight: 300, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              pr: 0.5,
              mb: 1.5,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {roles
                .filter((r) => 
                  !filtroRoles || 
                  r.descripcion.toLowerCase().includes(filtroRoles.toLowerCase()) ||
                  r.id.toString().includes(filtroRoles)
                )
                .map((r) => {
                const isAdmin = r.id === 1
                return (
                  <Box 
                    key={r.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.75, 
                      p: 0.75, 
                      bgcolor: isAdmin ? 'rgba(211, 47, 47, 0.05)' : 'white', 
                      borderRadius: 0.75, 
                      border: isAdmin ? '2px solid rgba(211, 47, 47, 0.3)' : '1px solid #eee',
                      position: 'relative'
                    }}
                  >
                    {isAdmin && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <AdminPanelSettingsIcon sx={{ fontSize: 14, color: '#d32f2f' }} />
                        <Chip 
                          label="Administrador" 
                          size="small" 
                          color="error"
                          sx={{ 
                            height: 18, 
                            fontSize: '0.65rem',
                            fontWeight: 600
                          }} 
                        />
                      </Box>
                    )}
                    <TextField 
                      size="small" 
                      label={`Rol ${r.id}`} 
                      value={r.descripcion}
                      disabled={isAdmin}
                      onChange={(e) => {
                        if (!isAdmin) {
                          const val = e.target.value
                          setRoles((prev) => prev.map((x) => x.id === r.id ? { ...x, descripcion: val } : x))
                        }
                      }}
                      sx={{ 
                        flex: 1,
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#d32f2f',
                          fontWeight: 600,
                        }
                      }}
                    />
                    {!isAdmin && (
                      <>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={async () => {
                            try {
                              await updateRolUsuario(r.id, r.descripcion)
                              const rs = await getRolUsuario()
                              setRoles(rs || [])
                              setGuardarSuccess("Rol actualizado correctamente")
                              setTimeout(() => setGuardarSuccess(null), 3000)
                            } catch {
                              setGuardarError("No se pudo actualizar la descripción del rol")
                            }
                          }}
                          sx={{
                            minWidth: 70,
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                          }}
                        >
                          Guardar
                        </Button>
                        <Button 
                          size="small" 
                          color="error" 
                          variant="outlined" 
                          onClick={async () => {
                            try {
                              setGuardarError(null)
                              setUsuariosAEliminar([])
                              setRolAEliminar(r)
                              const lista = await getUsuariosByRol(r.id)
                              setUsuariosAEliminar(lista || [])
                              setConfirmDeleteOpen(true)
                            } catch {
                              setGuardarError("No se pudieron obtener usuarios del rol")
                            }
                          }}
                          sx={{ 
                            minWidth: 70,
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                          }}
                        >
                          Eliminar
                        </Button>
                      </>
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>

          <Box sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mt: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: "#5b5b5f", fontSize: '0.8rem' }}>Copiar accesos</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Rol origen:</Typography>
            <Autocomplete
              size="small"
              options={roles}
              getOptionLabel={(opt) => opt.descripcion}
              value={rolOrigen}
              onChange={(e, val) => setRolOrigen(val)}
              renderInput={(params) => <TextField {...params} label="Rol origen" size="small" />}
              sx={{ minWidth: 150, width: 'auto' }}
            />
            <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.75rem' }}>Rol destino:</Typography>
            <Autocomplete
              size="small"
              options={roles}
              getOptionLabel={(opt) => opt.descripcion}
              value={rolDestino}
              onChange={(e, val) => setRolDestino(val)}
              renderInput={(params) => <TextField {...params} label="Rol destino" size="small" />}
              sx={{ minWidth: 150, width: 'auto' }}
            />
            <Button size="small" variant="contained" disabled={!rolOrigen || !rolDestino || guardando} onClick={async () => {
              try {
                setGuardando(true)
                setGuardarError(null)
                setGuardarSuccess(null)
                const accesosOrigen = await getRolAccesoByIdRol(rolOrigen.id)
                const activosSet = new Set(accesosOrigen.filter(a => a.eliminado === false).map(a => a.idMenuAcceso))
                const payload = menuAccesos.map(m => ({ id: m.id, activo: activosSet.has(m.id) }))
                await actualizarRolAccesos(rolDestino.id, payload)
                setGuardarSuccess("Accesos copiados correctamente")
                if (rolSeleccionado?.id === rolDestino.id) {
                  await cargarAccesosDelRol(rolDestino.id)
                }
              } catch {
                setGuardarError("No se pudieron copiar los accesos")
              } finally {
                setGuardando(false)
              }
            }} sx={{ fontSize: '0.7rem', py: 0.4, px: 1 }}>Copiar</Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirmación de eliminación de rol y usuarios */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Eliminar rol y usuarios asociados</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Vas a eliminar el rol <strong>{rolAEliminar?.descripcion}</strong> y los siguientes usuarios quedarán marcados como eliminados:
          </Typography>
          {usuariosAEliminar.length === 0 ? (
            <Alert severity="info">No se encontraron usuarios activos con este rol.</Alert>
          ) : (
            <Box sx={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
              {usuariosAEliminar.map((u) => (
                <Typography key={u.id} variant="body2">• {u.usuario} - {u.nombre} {u.apellido}</Typography>
              ))}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción realiza un borrado lógico (eliminado = true) de los usuarios listados y del rol.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" disabled={!rolAEliminar || guardando} onClick={async () => {
            try {
              setGuardando(true)
              setGuardarError(null)
              setGuardarSuccess(null)
              await deleteRolYUsuarios(rolAEliminar.id)
              setGuardarSuccess("Rol y usuarios eliminados correctamente")
              const rs = await getRolUsuario()
              setRoles(rs || [])
              // Si el rol eliminado es el seleccionado, limpiar selección y vista
              if (rolSeleccionado?.id === rolAEliminar.id) {
                setRolSeleccionado(null)
                setOpcionesAcceso([])
              }
              setConfirmDeleteOpen(false)
              setOpenRolesModal(false)
            } catch {
              setGuardarError("No se pudo eliminar el rol")
            } finally {
              setGuardando(false)
            }
          }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
      </Paper>
    </Container>
  )
}

export default GestionarAccesosComponent
